declare global {
  interface Window {
    fbq: any;
    _fbq: any;
    dataLayer: any[];
  }
}

let isPixelInitialized = false;

// Initialize the browser-side Meta Pixel
export const initMetaPixel = (pixelId: string) => {
  if (isPixelInitialized || !pixelId) return;

  // Initialize the fbq function queue even if already loaded or partially loaded
  (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  console.log(`[Meta Tracking] Initializing fbq for Pixel ID: ${pixelId}`);
  window.fbq('init', pixelId);
  isPixelInitialized = true;
};

// Generates a unique event ID for deduplication between Pixel and Conversions API
export const generateEventId = () => {
  return 'event_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now();
};

interface TrackMetaEventOptions {
  eventName: string;
  customData?: any;
  userData?: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
  };
}

// Track an event via both Browser Pixel, GTM DataLayer, and Conversions API (CAPI)
export const trackMetaEvent = async ({
  eventName,
  customData = {},
  userData = {}
}: TrackMetaEventOptions) => {
  console.log(`[Meta Tracking] Triggered event: "${eventName}"`);

  // Check GDPR consent before any tracking
  const consent = localStorage.getItem('cookieConsent');
  if (consent !== 'accepted') {
    console.warn(`[Meta Tracking] Event "${eventName}" skipped because cookieConsent is not "accepted". Current status: "${consent}"`);
    return { success: false, message: 'Consent not granted' };
  }

  const eventId = generateEventId();
  const pixelId = import.meta.env.VITE_META_PIXEL_ID;

  console.log(`[Meta Tracking] Cookie consent verified. Pixel ID: ${pixelId ? 'found' : 'NOT found'}. Event ID generated: ${eventId}`);

  // Initialize Pixel if it wasn't already initialized
  if (pixelId) {
    initMetaPixel(pixelId);
  } else {
    console.warn(`[Meta Tracking] VITE_META_PIXEL_ID is not configured in client environment variables.`);
  }

  // 1. Google Tag Manager / GA4 DataLayer integration
  window.dataLayer = window.dataLayer || [];
  const dataLayerPayload = {
    event: eventName, // Standard event name e.g., 'PageView', 'Lead'
    eventId: eventId,
    metaEventId: eventId,
    metaPixelId: pixelId,
    ...customData
  };
  console.log(`[Meta Tracking] Pushing to GTM dataLayer:`, dataLayerPayload);
  window.dataLayer.push(dataLayerPayload);

  // 2. Browser-side tracking (Meta Pixel)
  if (window.fbq) {
    console.log(`[Meta Tracking] Sending browser-side Pixel event: "${eventName}"`, { customData, eventID: eventId });
    window.fbq('track', eventName, customData, { eventID: eventId });
  } else {
    // If window.fbq doesn't exist yet (e.g. fbevents.js is still loading), we can still queue it
    console.warn(`[Meta Tracking] window.fbq is not fully loaded. Initializing queue fallback.`);
    if (!window.fbq) {
      window.fbq = function() {
        window.fbq.callMethod ? window.fbq.callMethod.apply(window.fbq, arguments) : window.fbq.queue.push(arguments);
      };
      if (!window._fbq) window._fbq = window.fbq;
      window.fbq.push = window.fbq;
      window.fbq.loaded = !0;
      window.fbq.version = '2.0';
      window.fbq.queue = [];
    }
    if (pixelId) {
      window.fbq('init', pixelId);
    }
    window.fbq('track', eventName, customData, { eventID: eventId });
  }

  // 3. Server-side tracking (Meta Conversions API)
  try {
    console.log(`[Meta Tracking] Sending server-side Conversions API (CAPI) event: "${eventName}"...`);
    const response = await fetch('/api/meta-event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventName,
        eventId,
        eventSourceUrl: window.location.href,
        userData,
        customData,
      }),
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      console.error(`[Meta Tracking] Server CAPI call failed with status ${response.status}:`, result);
      return { success: false, error: result };
    }
    
    console.log(`[Meta Tracking] Server CAPI call succeeded! Response:`, result);
    return result;
  } catch (error) {
    console.error(`[Meta Tracking] Error sending Meta CAPI event:`, error);
    return { success: false, error };
  }
};
